import Ajv, { ErrorObject, SchemaObject } from 'ajv/dist/2020';
import { JSONSchema7, JSONSchema7Definition } from 'json-schema';
import { cloneDeep, get, isObject, omit, pick } from 'lodash';
import { nanoid } from 'nanoid';
import { Config, PollConfig, PollTrait } from '../configFields';
import {
  AnswerDeck,
  Group,
  Set as PollSet,
  QuestionDeck,
  SYSTEM_SCREEN as SystemScreenType,
} from '../types/poll.generated';
import { Poll } from '../types/poll.generated';
import { createICStateSchema } from './icStateSchema';
import { SchemaEngine } from './index';
import { SchemaTrait } from './traits';

type QuestionSchema =
  | {
      properties: {
        data: {
          properties: {
            answers?: {
              items: Record<string, unknown>;
            };
            [x: string]: unknown;
          };
        };
      };
      definitions?: Record<string, unknown>;
    }
  | {
      properties: {
        data: {
          allOf: unknown[] | readonly unknown[];
        };
      };
      definitions?: Record<string, unknown>;
    };

type QuestionsMapping = Record<
  string,
  {
    config: PollConfig;
    schema: QuestionSchema;
    generator: {
      generateQuestion: (
        answers: any[],
        overrides: any,
      ) => { id: string; data: { type: string; answers?: object[] }; groupId?: string; setId?: string };
      generateAnswer?: (overrides: any) => unknown;
    };
  }
>;

type WelcomesMapping = Record<
  string,
  {
    config: Config;
    schema: unknown;
    generator: {
      generateWelcome: (overrides: any) => { id: string; data: { type: string; answers?: object[] } };
    };
  }
>;

type SystemScreensMapping = Record<
  string,
  {
    config: Config;
    schema: unknown;
    generator: {
      generateSystemScreen: (
        overrides: Partial<SystemScreenType['data']>,
        cause: SystemScreenType['data']['cause'],
      ) => SystemScreenType;
    };
  }
>;

type FinalsMapping = Record<
  string,
  {
    config: Config;
    schema: unknown;
  }
>;

type QuestionType<Questions extends QuestionsMapping, T extends keyof Questions> = T extends T
  ? ReturnType<Questions[T]['generator']['generateQuestion']>
  : never;

type WelcomeType<Welcomes extends WelcomesMapping, T extends keyof Welcomes> = T extends T
  ? ReturnType<Welcomes[T]['generator']['generateWelcome']>
  : never;

function resolveAllLocalRefs(schema: JSONSchema7, rootSchema: JSONSchema7 = schema): JSONSchema7 {
  return {
    ...schema,
    allOf: schema.allOf?.map(v => {
      if (typeof v === 'object' && v.$ref) {
        if (!v.$ref.match(/#(\/\w+)+/)) throw new Error('Unsupported $ref!');
        const refdSchema = get(rootSchema, v.$ref.split('/').slice(1).join('.')) as JSONSchema7 | undefined;
        if (refdSchema) return refdSchema;
        throw new Error('Dangling $ref!');
      }
      return v;
    }),
  };
}

function getAllProperties(schema: JSONSchema7, rootSchema: JSONSchema7 = schema): Set<string> {
  const acc = new Set<string>();

  traverseDirectProperties(
    schema,
    key => {
      acc.add(key);
    },
    rootSchema,
  );

  return acc;
}

function traverseDirectProperties(
  schema: JSONSchema7,
  cb: (key: string, value: JSONSchema7Definition) => void,
  rootSchema: JSONSchema7 = schema,
  basePath = '',
) {
  if (typeof schema?.$ref === 'string') {
    if (!schema.$ref.match(/#(\/\w+)+/)) throw new Error('Unsupported $ref!');
    const refdSchema = get(rootSchema, schema.$ref.split('/').slice(1).join('.')) as JSONSchema7 | undefined;
    if (refdSchema) return traverseDirectProperties(refdSchema, cb, rootSchema, basePath);
    throw new Error('Dangling $ref! ' + schema.$ref);
  }

  if (schema.properties) {
    for (const [key, value] of Object.entries(schema.properties)) {
      cb(basePath ? `${basePath}.${key.toString()}` : key.toString(), value);

      if (isObject(value)) {
        traverseDirectProperties(value, cb, schema.properties, key.toString());
      }
    }
  }
  for (const subschema of [
    ...(schema.allOf ?? []),
    ...(schema.oneOf ?? []),
    ...(schema.anyOf ?? []),
    schema.if,
    schema.else,
    schema.then,
  ]) {
    if (typeof subschema === 'object') {
      traverseDirectProperties(subschema, cb, rootSchema, basePath);
    }
  }
}

abstract class AdminEngine<
  Questions extends QuestionsMapping,
  Finals extends FinalsMapping,
  Welcomes extends WelcomesMapping,
  SystemScreens extends SystemScreensMapping,
  Flavours extends readonly string[],
> implements SchemaEngine
{
  protected abstract flavours: Flavours;
  protected abstract finalsMapping: Finals;
  protected abstract welcomesMapping: Welcomes;
  protected abstract questionsMapping: Questions;
  protected abstract screensMapping: SystemScreens;
  protected abstract schemaTraits: SchemaTrait[];
  protected abstract groupSchema: JSONSchema7;
  protected abstract deckSchema: JSONSchema7;
  protected abstract setSchema: JSONSchema7;

  abstract getTemplate(): Poll['template'];
  abstract getSchema(): unknown;
  abstract generatePoll(): { questions: { data: { type: string } }[] };

  getQuestionTypes() {
    return this.getMappingTypes(this.questionsMapping);
  }

  getWelcomeTypes() {
    return this.getMappingTypes(this.welcomesMapping);
  }

  getFinalTypes() {
    return this.getMappingTypes(this.finalsMapping);
  }

  getFlavours() {
    return this.flavours;
  }

  getICStateSchema() {
    return createICStateSchema();
  }

  getQuestionPropertySchema(type: string, property: string): JSONSchema7[] {
    const schema = this.getQuestionSchemaByType(type);

    if (!schema) return [];

    const acc: JSONSchema7[] = [];

    traverseDirectProperties(
      (schema as { properties?: { data?: JSONSchema7 } })?.properties?.data ?? {},
      (key, value) => {
        if (typeof value === 'object' && key === property) acc.push(value);
      },
      schema,
    );

    return acc;
  }

  changeWelcomeType<T extends keyof Welcomes>(
    welcome: WelcomeType<Welcomes, keyof Welcomes>,
    nextType: T,
  ): WelcomeType<Welcomes, T> {
    const cleanedWelcome = deepRemoveUnevaluatedPropertiesFromWelcome({ engine: this, type: nextType, welcome });
    const defaultWelcome = this.generateWelcome(nextType);

    const newWelcome = {
      ...cleanedWelcome,
      data: {
        ...defaultWelcome.data,
        ...cleanedWelcome.data,
      },
    } as WelcomeType<Welcomes, T>;

    return newWelcome;
  }

  changeQuestionType<T extends keyof Questions>(
    question: QuestionType<Questions, keyof Questions>,
    nextType: T,
  ): QuestionType<Questions, T> {
    const propertiesToOmitOnTypeChange = ['preset'];

    const cleanedQuestion = deepRemoveUnevaluatedPropertiesFromQuestion({ engine: this, type: nextType, question });
    const defaultQuestion =
      this.hasTrait(nextType, 'HIDDEN_QUESTION_TYPE') ||
      this.hasTrait(nextType, 'WITHOUT_ANSWERS_TYPE') ||
      this.hasTrait(nextType, 'ONLY_EXCLUSIVE_ANSWERS')
        ? this.generateQuestion(nextType, [])
        : this.generateQuestion(nextType, [this.generateAnswer(nextType), this.generateAnswer(nextType)]);

    let questionAnswers = cleanedQuestion.data.answers;
    const defaultQuestionAnswers = defaultQuestion.data.answers;

    /** Убираем не эксклюзивные ответы для вопросов с ONLY_EXCLUSIVE_ANSWERS */
    if (this.hasTrait(nextType, 'ONLY_EXCLUSIVE_ANSWERS')) {
      const previousAnswers = questionAnswers || [];
      questionAnswers = previousAnswers.filter(
        answer => 'exclusiveAnswer' in answer && answer.exclusiveAnswer === true,
      );
    }

    const newQuestion = {
      ...cleanedQuestion,
      data: {
        ...defaultQuestion.data,
        ...omit(cleanedQuestion.data, propertiesToOmitOnTypeChange),
        ...(questionAnswers && {
          answers: questionAnswers.length
            ? questionAnswers.map(a => ({ ...defaultQuestionAnswers![0], ...a }))
            : defaultQuestionAnswers,
        }),
      },
    } as QuestionType<Questions, T>;

    return newQuestion;
  }

  getAnswerProperties<T extends keyof Questions>(type: T): string[] {
    const schema = this.getAnswerSchemaByType(type);
    return schema ? Array.from(getAllProperties(schema)) : [];
  }

  getQuestionProperties<T extends keyof Questions>(type: T): string[] {
    const schema = this.getQuestionSchemaByType(type) as { properties: { data: JSONSchema7 } } | undefined;
    return schema ? Array.from(getAllProperties(schema?.properties?.data, schema)) : [];
  }

  getWelcomeProperties<T extends keyof Welcomes>(type: T): string[] {
    const schema = this.getWelcomeSchemaByType(type) as { properties: { data: JSONSchema7 } } | undefined;
    return schema ? Array.from(getAllProperties(schema?.properties?.data, schema)) : [];
  }

  getSystemScreenProperties<T extends keyof SystemScreens>(type: T): string[] {
    const schema = this.getSystemScreenByType(type) as { properties: { data: JSONSchema7 } } | undefined;
    return schema ? Array.from(getAllProperties(schema?.properties?.data, schema)) : [];
  }

  getFinalProperties<T extends keyof Finals>(type: T): string[] {
    const schema = this.getFinalSchemaByType(type) as { properties: { data: JSONSchema7 } } | undefined;
    return schema ? Array.from(getAllProperties(schema?.properties?.data, schema)) : [];
  }

  getGroupProperties(): string[] {
    const schema = this.groupSchema as { properties: { data: JSONSchema7 } };
    return Array.from(getAllProperties(schema?.properties?.data, schema));
  }

  getSetProperties(): string[] {
    const schema = this.setSchema as { properties: { data: JSONSchema7 } };
    return Array.from(getAllProperties(schema?.properties?.data, schema));
  }

  getDeckProperties(): string[] {
    const schema = this.deckSchema as { properties: { data: JSONSchema7 } };
    return Array.from(getAllProperties(schema?.properties?.data, schema));
  }

  getQuestionMappingByType<T extends keyof Questions>(type: T): Questions[T] {
    const questionMapping = this.questionsMapping[type];
    if (!questionMapping) {
      throw new Error(
        `The matching of question mapping for the type ${String(type)} cannot be found. ` +
          `questionsMapping: ${JSON.stringify(this.questionsMapping)}`,
      );
    }
    return questionMapping;
  }

  getQuestionSchemaByType<T extends keyof Questions>(type: T): JSONSchema7 | null {
    return this.getQuestionMappingByType(type).schema as JSONSchema7 | null;
  }

  getFinalMappingByType<T extends keyof Finals>(type: T): Finals[T] {
    const finalMapping = this.finalsMapping[type];
    if (!finalMapping) {
      throw new Error(
        `The matching of final mapping for the type ${String(type)} cannot be found. ` +
          `finalsMapping: ${JSON.stringify(this.finalsMapping)}`,
      );
    }
    return finalMapping;
  }

  getFinalSchemaByType<T extends keyof Finals>(type: T): Finals[T]['schema'] {
    return this.getFinalMappingByType(type).schema;
  }

  getWelcomeMappingByType<T extends keyof Welcomes>(type: T): Welcomes[T] {
    const welcomeMapping = this.welcomesMapping[type];
    if (!welcomeMapping) {
      throw new Error(
        `The matching of welcome mapping for the type ${String(type)} cannot be found. ` +
          `welcomesMapping: ${JSON.stringify(this.welcomesMapping)}`,
      );
    }
    return welcomeMapping;
  }

  getWelcomeSchemaByType<T extends keyof Welcomes>(type: T): Welcomes[T]['schema'] {
    return this.getWelcomeMappingByType(type).schema;
  }

  getSystemScreenMappingByType<T extends keyof SystemScreens>(type: T): SystemScreens[T] {
    const systemMapping = this.screensMapping[type];

    if (!systemMapping) {
      throw new Error(
        `The matching of screens mapping for the type ${String(type)} cannot be found. ` +
          `screensMapping: ${JSON.stringify(this.welcomesMapping)}`,
      );
    }

    return systemMapping;
  }

  getSystemScreenByType<T extends keyof SystemScreens>(type: T): SystemScreens[T]['schema'] {
    return this.getSystemScreenMappingByType(type).schema;
  }

  getAnswerSchemaByType<T extends keyof Questions>(type: T): JSONSchema7 | null {
    const schema = this.getQuestionMappingByType(type).schema;
    const items =
      'properties' in schema.properties.data
        ? schema.properties.data.properties?.answers?.items
        : (schema.definitions?.[`${String(type)}_ANSWER`] as { allOf: { $ref: `#/definitions/${string}` }[] }) ||
          (schema.properties.data.allOf[0] as { properties: { answers?: { items: any } } }).properties.answers?.items;
    return items ? ({ ...items, definitions: schema.definitions } as any) : null;
  }

  entriesFromSchema(mapping: Record<string, unknown>, schema: { properties: Record<string, unknown> }) {
    const regKeyIsArray = /^([a-zA-Z]+)\[[0-9]+\]$/;
    return Object.entries(mapping).filter(([key]) => {
      key = key.split('.')[0]!;
      const keyIsArray = regKeyIsArray.exec(key);
      if (keyIsArray !== null) {
        key = keyIsArray[1]!;
      }
      return !!schema.properties[key];
    });
  }

  hasSchemaTrait(trait: SchemaTrait) {
    return this.schemaTraits.includes(trait);
  }

  hasTrait(type: keyof Questions, trait: PollTrait) {
    return this.getQuestionMappingByType(type).config.traits.includes(trait);
  }

  generateQuestion<T extends keyof Questions>(type: T, answers: unknown[], overrides?: Partial<unknown>) {
    const { generateQuestion } = this.getQuestionMappingByType(type).generator;
    return generateQuestion(answers, overrides) as ReturnType<Questions[T]['generator']['generateQuestion']>;
  }

  generateAnswer<T extends keyof Questions>(type: T, overrides?: Partial<unknown>) {
    const { generateAnswer } = this.getQuestionMappingByType(type).generator;
    return generateAnswer?.(overrides) as Questions[T]['generator']['generateAnswer'] extends undefined
      ? undefined
      : ReturnType<NonNullable<Questions[T]['generator']['generateAnswer']>>;
  }

  generateWelcome<T extends keyof Welcomes>(type: T, overrides?: Partial<unknown>) {
    const { generateWelcome } = this.getWelcomeMappingByType(type).generator;
    return generateWelcome(overrides) as ReturnType<Welcomes[T]['generator']['generateWelcome']>;
  }

  generateGroup(overrides?: Partial<Group>): Group {
    return {
      id: nanoid(10),
      ...overrides,
      data: {
        autoNext: false,
        autoCommit: false,
        randomizeGroup: false,
        hasTimer: false,
        logicOperationType: 'OR',
        logicRules: [],
        hasSequentialDisplay: false,
        instantStatisticRequired: false,
        ...overrides?.data,
      },
    };
  }

  generateSet(overrides?: Partial<PollSet>): PollSet {
    return {
      id: nanoid(10),
      ...overrides,
      data: {
        randomizeSet: false,
        type: 'default',
        exclusiveAnswers: [],
        instantStatisticRequired: false,
        logicOperationType: 'OR',
        logicRules: [],
        ...overrides?.data,
      },
    };
  }

  generateQuestionDeck(
    overrides: Partial<Pick<QuestionDeck, 'id'>> & Required<Omit<QuestionDeck, 'id'>>,
  ): QuestionDeck {
    return {
      id: nanoid(10),
      ...overrides,
      data: {
        logicRules: [],
        logicOperationType: 'OR',
        randomizeDeck: false,
        instantStatisticRequired: false,
        ...overrides['data'],
      },
    };
  }

  generateAnswerDeck(overrides: { questionId: string } & Partial<AnswerDeck>): AnswerDeck {
    return {
      id: nanoid(10),
      ...overrides,
      data: {
        randomizeDeck: false,
        logicOperationType: 'OR',
        logicRules: [],
        instantStatisticRequired: false,
        ...overrides.data,
      },
    };
  }

  protected normalizePoll<
    T extends { useServerPass?: boolean; finals: unknown[]; questions: { id: string; groupId?: string }[] },
  >(poll: T): T & { firstId: string } {
    const firstQuestion = poll.questions.length > 0 ? poll.questions[0] : null;
    if (firstQuestion) {
      return {
        ...poll,
        firstId: firstQuestion.groupId || firstQuestion.id,
      };
    }
    if (poll.useServerPass) {
      return { ...poll, firstId: '' };
    }
    throw new Error('No questions were found in poll!');
  }

  protected getMappingTypes(mapping: Record<string, { config: Config }>) {
    return Object.entries(mapping).reduce(
      (acc, [key, value]) => {
        acc[key] = value.config.title;
        return acc;
      },
      {} as Record<string, string>,
    );
  }
}

export default AdminEngine;

export const deepRemoveUnevaluatedPropertiesFromQuestion = <
  Questions extends QuestionsMapping,
  Finals extends FinalsMapping,
  Welcomes extends WelcomesMapping,
  SystemScreens extends SystemScreensMapping,
  Flavours extends readonly string[],
>({
  engine,
  type,
  question,
}: {
  engine: AdminEngine<Questions, Finals, Welcomes, SystemScreens, Flavours>;
  type: keyof Questions;
  question: { data: Record<string, unknown> & { answers?: object[] } };
}) => {
  let newQuestion = cloneDeep(question);

  const ajv = new Ajv({
    allErrors: true,
    discriminator: true,
    allowUnionTypes: true,
    removeAdditional: false,
    unevaluated: true,
  });

  const answerJsonSchema = engine.getAnswerSchemaByType(type);
  const questionJsonSchema = engine.getQuestionSchemaByType(type) as {
    definitions: Record<string, JSONSchema7>;
    properties: { data: JSONSchema7 };
  };

  reduceAnswers(newQuestion);
  reduceQuestion(newQuestion);

  function reduceQuestion(question: { data: Record<string, unknown> }) {
    const questionSchema = resolveAllLocalRefs(
      {
        ...questionJsonSchema.properties.data,
        definitions: questionJsonSchema.definitions,
        additionalProperties: undefined,
        ...{ unevaluatedProperties: false },
      },
      questionJsonSchema,
    );

    const withoutAdditionalPropertiesRules: JSONSchema7 = JSON.parse(
      JSON.stringify(questionSchema),
      (key, value: unknown) =>
        key === 'unevaluatedProperties' ? false : key === 'additionalProperties' ? undefined : value,
    );

    const isValid = ajv.validate(withoutAdditionalPropertiesRules, question.data);
    if (!isValid && ajv.errors) removeAdditional(question.data, ajv.errors, withoutAdditionalPropertiesRules);
  }

  function reduceAnswers(question: { data: { answers?: object[] } }) {
    if (question.data.answers) {
      if (answerJsonSchema === null) {
        question.data.answers = [];
      } else {
        question.data.answers = question.data.answers.map(answer => reduceAnswer(answer as Record<string, unknown>));
      }
    }
  }

  function reduceAnswer(answer: Record<string, unknown>) {
    const answerSchema = { ...answerJsonSchema, unevaluatedProperties: false };
    const isValid = ajv.validate(answerSchema, answer);
    if (!isValid && ajv.errors) removeAdditional(answer, ajv.errors, answerSchema);

    return answer;
  }

  return { ...newQuestion, data: { ...newQuestion.data, type } };
};

export const deepRemoveUnevaluatedPropertiesFromWelcome = <
  Questions extends QuestionsMapping,
  Finals extends FinalsMapping,
  Welcomes extends WelcomesMapping,
  SystemScreens extends SystemScreensMapping,
  Flavours extends readonly string[],
>({
  engine,
  type,
  welcome,
}: {
  engine: AdminEngine<Questions, Finals, Welcomes, SystemScreens, Flavours>;
  type: keyof Welcomes;
  welcome: { data: Record<string, unknown> };
}) => {
  let newWelcome = cloneDeep({ ...welcome, data: { ...welcome.data, type } });

  const ajv = new Ajv({
    allErrors: true,
    discriminator: true,
    allowUnionTypes: true,
    removeAdditional: false,
    unevaluated: true,
  });

  const welcomeJsonSchema = engine.getWelcomeSchemaByType(type) as {
    definitions: Record<string, JSONSchema7>;
    properties: { data: JSONSchema7 };
  };

  reduceWelcome(newWelcome);

  function reduceWelcome(welcome: { data: Record<string, unknown> }) {
    const welcomeSchema = resolveAllLocalRefs(
      {
        ...welcomeJsonSchema.properties.data,
        definitions: welcomeJsonSchema.definitions,
        additionalProperties: undefined,
        ...{ unevaluatedProperties: false },
      },
      welcomeJsonSchema,
    );

    const withoutAdditionalPropertiesRules: JSONSchema7 = JSON.parse(
      JSON.stringify(welcomeSchema),
      (key, value: unknown) =>
        key === 'unevaluatedProperties' ? false : key === 'additionalProperties' ? undefined : value,
    );

    const isValid = ajv.validate(welcomeSchema, welcome.data);
    if (!isValid && ajv.errors) removeAdditional(welcome.data, ajv.errors, withoutAdditionalPropertiesRules);
  }

  return newWelcome;
};

function removeAdditional(data: Record<string, unknown>, errors: ErrorObject[], schema: SchemaObject) {
  const ajv = new Ajv({
    allErrors: true,
    discriminator: true,
    allowUnionTypes: true,
    removeAdditional: true,
    unevaluated: true,
  });

  errors.forEach(error => {
    if (error.params?.unevaluatedProperty && error.keyword === 'unevaluatedProperties') {
      // istanbul ignore else
      if (Array.isArray(schema.allOf)) {
        const ifThenElseRule = schema.allOf.find(it => error.params.unevaluatedProperty in getThenElseProperties(it));
        if (ifThenElseRule) {
          const ruleFields = pickFieldsFromRule(ifThenElseRule, data);
          ajv.validate({ ...ifThenElseRule, unevaluatedProperties: false }, ruleFields);
          if (findInAjvErrors(ajv.errors, error.params.unevaluatedProperty)) {
            for (const field of Object.keys(ruleFields)) {
              delete data[field];
            }
          }
          return;
        }
      }

      delete data[error.params.unevaluatedProperty];
    }
  });
}

function findInAjvErrors(ajvErrors: Ajv['errors'], field: string) {
  return (
    ajvErrors && ajvErrors.find(it => it.params.unevaluatedProperty === field || it.params.additionalProperty === field)
  );
}

type RuleItem = {
  properties?: Record<string, unknown>;
};

type Rule = {
  then: RuleItem;
  if: RuleItem;
  else: RuleItem;
};

function pickFieldsFromRule(rule: Rule, data: Record<string, unknown>) {
  const keys = [...Object.keys(rule.if?.properties ?? {}), ...Object.keys(getThenElseProperties(rule))];
  return pick(data, keys);
}

function getThenElseProperties(rule: Rule) {
  const result: Record<string, unknown> = {};
  if (rule.then?.properties) Object.assign(result, rule.then.properties);
  if (rule.else?.properties) Object.assign(result, rule.else.properties);
  return result;
}
